import { v7 as uuidv7 } from 'uuid';
import { getDexieStore } from './dexie.js';
import { enqueue } from './outbox.js';
import type { CachedShot, SyncStore } from './store.js';

type ShotFields = Pick<
  CachedShot,
  'valueNumeric' | 'zoneCode' | 'firstShotTimeMs' | 'elapsedTimeMs' | 'source'
>;
export async function queueShot(
  input: {
    assignmentId: string;
    shotCount: number;
    fields: (ord: number) => ShotFields;
    /** Use this ordinal when it is free (undo of a delete); else the lowest free one. */
    preferredOrd?: number;
  },
  store: SyncStore = getDexieStore(),
): Promise<CachedShot | null> {
  return store.transaction(async (tx) => {
    const current = await tx.shotsForAssignment(input.assignmentId);
    if (input.shotCount > 0 && current.length >= input.shotCount) return null;
    const used = new Set(current.map((s) => s.ord));
    let ord = 1;
    if (input.preferredOrd != null && input.preferredOrd >= 1 && !used.has(input.preferredOrd)) {
      ord = input.preferredOrd;
    } else {
      while (used.has(ord)) ord++;
    }
    const key = uuidv7();
    const shot: CachedShot = {
      ...input.fields(ord),
      id: key,
      clientIdempotencyKey: key,
      roundAssignmentId: input.assignmentId,
      ord,
      recordedAt: Date.now(),
    };
    await tx.putShot(shot);
    await enqueue(
      'shot.add',
      {
        roundAssignmentId: input.assignmentId,
        ord,
        clientIdempotencyKey: key,
        valueNumeric: shot.valueNumeric,
        zoneCode: shot.zoneCode,
        ...(shot.firstShotTimeMs != null ? { firstShotTimeMs: shot.firstShotTimeMs } : {}),
        ...(shot.elapsedTimeMs != null ? { elapsedTimeMs: shot.elapsedTimeMs } : {}),
        ...(shot.source ? { source: shot.source } : {}),
      },
      tx,
    );
    return shot;
  });
}

/**
 * `lastError` an app writes when it clears a pause marker after a new sign-in
 * (instead of erasing it): the entry may already have reached the server
 * before the pause, so it must keep counting as "possibly submitted" — an
 * erased lastError + returned attempt would make it look never sent and a
 * later correction would be coalesced into it and lost.
 */
export const RESUMED_AFTER_SIGN_IN = 'resumed_after_sign_in';

/** A never-submitted add can be changed locally. Otherwise preserve causal order. */
export async function correctQueuedShot(
  assignmentId: string,
  key: string,
  patch: Partial<ShotFields> | null,
  store: SyncStore = getDexieStore(),
): Promise<void> {
  await store.transaction(async (tx) => {
    const shot = (await tx.shotsForAssignment(assignmentId)).find(
      (s) => s.clientIdempotencyKey === key,
    );
    if (!shot) {
      if (patch === null) return; // Watch undo deliveries can be repeated.
      throw new Error('shot_not_found');
    }
    const entries = await tx.listPendingAndInflight();
    const add = entries.find((e) => {
      const p = e.payload as { roundAssignmentId?: string; clientIdempotencyKey?: string };
      return (
        e.op === 'shot.add' &&
        p.roundAssignmentId === assignmentId &&
        p.clientIdempotencyKey === key
      );
    });
    // "Never submitted" = no attempt counted AND no error recorded. A paused
    // attempt (network loss, timeout, 502-504, 401) gives its attempt back but
    // keeps `lastError`; the server may already have saved that add, so a
    // correction must go out as its own update/remove, not be coalesced.
    if (add?.status === 'pending' && add.attempts === 0 && add.lastError == null) {
      await tx.updateOutbox(
        add.id,
        patch === null
          ? { status: 'acked' }
          : { payload: { ...(add.payload as object), ...patch } },
      );
    } else {
      await enqueue(
        patch === null ? 'shot.remove' : 'shot.update',
        {
          roundAssignmentId: assignmentId,
          clientIdempotencyKey: key,
          ...(patch ?? {}),
        },
        tx,
      );
    }
    if (patch === null) await tx.deleteShotByKey(assignmentId, key);
    else await tx.putShot({ ...shot, ...patch });
  });
}
